// The last column of an upload's results: what happened to the row's owner, and, for an
// approver, the "Assign anyway" over a forceable person-gate block.

import { useState } from 'react';
import { flagReasonsLine } from './bankRequest';
import { canForceRow, forceConfirmLine, intakeBlock } from './intakeBlocks';
import type { IntakeMode } from './intakeOwner';
import type { IntakeResultRow } from './intakeResults';

function BlockedReasons({ row, hard }: { row: IntakeResultRow; hard: boolean }) {
  const reasons = intakeBlock(row)?.reasons || [];
  const lead = hard ? "Can't be assigned" : 'Blocked';
  return (
    <span className="csvbad" title={reasons.join('; ')}>
      {lead} — {reasons.join('; ')}
    </span>
  );
}

export function IntakeAssignmentCell({
  row,
  mode,
  targetLabel,
  busy,
  onForce,
}: {
  row: IntakeResultRow;
  mode: IntakeMode;
  // Who the row was meant for, as the confirm names them.
  targetLabel: string;
  busy: boolean;
  onForce: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const block = intakeBlock(row);

  // Stopped by the person gate: every reason, and the force when this viewer may use it.
  if (block) {
    const forceable = canForceRow(row, mode);
    return (
      <div className="csvblock">
        <BlockedReasons row={row} hard={!block.forceable} />
        {forceable && !confirming ? (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => setConfirming(true)}
          >
            Assign anyway
          </button>
        ) : null}
        {forceable && confirming ? (
          <div className="csvconfirm" role="status">
            <p>{forceConfirmLine(row, targetLabel)}</p>
            <div className="csvconfirmactions">
              <button
                type="button"
                className="btn primary"
                disabled={busy}
                onClick={() => {
                  setConfirming(false);
                  onForce();
                }}
              >
                Assign anyway
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (row.assignStatus === 'written') {
    return <span className="requestmark">{row.assignMessage ? `${row.assignMessage} ✓` : 'Assigned ✓'}</span>;
  }
  if (row.assignStatus === 'requested') {
    const flagLine = flagReasonsLine(row);
    if (flagLine) {
      return (
        <span className="requestmark requestflagged" title={row.exclusion_detail || flagLine}>
          Requested ✓ — flagged for review: {flagLine}
        </span>
      );
    }
    return <span className="requestmark">Requested ✓ — waiting for approval</span>;
  }
  if (row.assignStatus === 'unresolved' || row.assignStatus === 'refused') {
    return <span className="csvbad">{row.assignMessage || 'Not assigned'}</span>;
  }
  if (row.assignMessage) return <>{row.assignMessage}</>;
  if (row.outcome === 'excluded') return <>—</>;
  return <>Left in the bank</>;
}
